import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { isUsableLot, type InventoryAttachment, type InventoryCategory, type InventoryItemSummary, type InventoryLot, type InventoryMovement } from '@jupi/shared'
import { apiUrl } from '../api/client'
import { ExpiryChip, useClubToday } from '../components/ExpiryChip'
import { formatDate } from '../format'
import { categoryName, formatMoney, formatValue } from './labels'

// One item with everything held of it. The actions are for whoever manages
// the inventory; a read-only holder sees the same facts without them.
export function ItemDetailDialog({
  item,
  category,
  canManage,
  language,
  onClose,
  onEdit,
  onArchive,
  onDelete,
  onAddLot,
  onEditLot,
  onDeleteLot,
  onAttach,
  onDeleteAttachment,
  onUse,
  movements,
}: {
  item: InventoryItemSummary | null
  category: InventoryCategory | undefined
  canManage: boolean
  language: string
  onClose: () => void
  onEdit: () => void
  onArchive: () => void
  onDelete: () => void
  onAddLot: () => void
  onEditLot: (lot: InventoryLot) => void
  onDeleteLot: (lot: InventoryLot) => void
  onAttach: (lot: InventoryLot) => void
  onDeleteAttachment: (attachment: InventoryAttachment) => void
  onUse: (lot: InventoryLot) => void
  movements: InventoryMovement[]
}) {
  const { t } = useTranslation()
  const today = useClubToday()
  if (!item) return null
  const fact = (label: string, value: string | null) =>
    value ? (
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {label}
        </Typography>
        <Typography>{value}</Typography>
      </Box>
    ) : null

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {item.name}{' '}
        {item.archivedAt ? <Chip size="small" label={t('inventory.archived')} /> : null}{' '}
        {item.lowStock ? <Chip size="small" color="warning" label={t('inventory.lowBadge')} /> : null}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={3} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {fact(t('inventory.category'), category ? categoryName(category, language) : item.categoryKey)}
            {fact(t('inventory.partNumber'), item.partNumber)}
            {fact(t('inventory.manufacturer'), item.manufacturer)}
            {fact(t('inventory.inStock'), `${item.usableQuantity} ${item.unit}`)}
            {fact(t('inventory.minQuantity'), item.minQuantity === null ? null : String(item.minQuantity))}
            {fact(t('inventory.value'), Object.keys(item.valueByCurrency).length ? formatValue(item.valueByCurrency, language) : null)}
          </Stack>
          {item.description ? <Typography sx={{ whiteSpace: 'pre-wrap' }}>{item.description}</Typography> : null}
          {item.fitments.length ? (
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('inventory.fits')}:
              </Typography>
              {item.fitments.map((f) => (
                <Chip key={f.fitmentId} size="small" variant="outlined" label={f.registration ?? f.aircraftType} />
              ))}
            </Stack>
          ) : null}
          {item.tags.length ? (
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
              {item.tags.map((tag) => (
                <Chip key={tag} size="small" label={tag} />
              ))}
            </Stack>
          ) : null}
          {item.notes ? (
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
              {item.notes}
            </Typography>
          ) : null}

          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">{t('inventory.lots')}</Typography>
            {canManage ? (
              <Button size="small" variant="outlined" onClick={onAddLot}>
                {t('inventory.addLot')}
              </Button>
            ) : null}
          </Stack>
          {item.lots.length === 0 ? (
            <Typography color="text.secondary">{t('inventory.noLots')}</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('inventory.quantity')}</TableCell>
                    <TableCell>{t('inventory.location')}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{t('inventory.serialNumber')}</TableCell>
                    <TableCell>{t('inventory.expiresOn')}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{t('inventory.unitPrice')}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{t('inventory.purchasedOn')}</TableCell>
                    <TableCell>{t('inventory.files')}</TableCell>
                    {canManage ? <TableCell /> : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {item.lots.map((lot) => (
                    <TableRow key={lot.lotId} sx={{ opacity: lot.quantity === 0 ? 0.55 : 1 }}>
                      <TableCell>
                        {lot.quantity} {item.unit}
                        {lot.condition !== 'new' ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {t(`inventory.conditions.${lot.condition}`)}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{lot.locationName ?? '—'}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {lot.serialNumber ?? ''}
                        {lot.batchNumber ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {lot.batchNumber}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {lot.expiresOn ? (
                          <>
                            {formatDate(lot.expiresOn)} {lot.quantity > 0 ? <ExpiryChip expiryDate={lot.expiresOn} /> : null}
                          </>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {lot.unitPrice === null ? '—' : formatMoney(lot.unitPrice, lot.currency, language)}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {lot.purchasedOn ? formatDate(lot.purchasedOn) : '—'}
                        {lot.supplier || lot.reference ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {[lot.supplier, lot.reference].filter(Boolean).join(' · ')}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                          {lot.attachments.map((a) => (
                            <Chip
                              key={a.attachmentId}
                              size="small"
                              variant="outlined"
                              component="a"
                              clickable
                              href={apiUrl(`/api/inventory/attachments/${a.attachmentId}`)}
                              target="_blank"
                              rel="noopener"
                              label={`${t(`inventory.attachKinds.${a.kind}`)}: ${a.filename}`}
                              onDelete={canManage ? () => onDeleteAttachment(a) : undefined}
                              sx={{ maxWidth: 220 }}
                            />
                          ))}
                          {canManage ? (
                            <Button size="small" onClick={() => onAttach(lot)}>
                              {t('inventory.attach')}
                            </Button>
                          ) : null}
                        </Stack>
                      </TableCell>
                      {canManage ? (
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {isUsableLot(lot, today) && lot.quantity > 0 ? (
                            <Button size="small" variant="contained" onClick={() => onUse(lot)}>
                              {t('inventory.use')}
                            </Button>
                          ) : null}
                          <Button size="small" onClick={() => onEditLot(lot)}>
                            {t('common.edit')}
                          </Button>
                          <Button size="small" color="error" onClick={() => onDeleteLot(lot)}>
                            {t('common.delete')}
                          </Button>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Typography variant="h6">{t('inventory.usage')}</Typography>
          {movements.length === 0 ? (
            <Typography color="text.secondary">{t('inventory.noUsage')}</Typography>
          ) : (
            <Stack spacing={0.5}>
              {movements.map((m) => (
                <Typography key={m.movementId} variant="body2">
                  {t('inventory.usedOn', { date: formatDate(m.usedAt), qty: m.quantity, unit: m.unit })}
                  {m.registration ? ` · ${m.registration}` : ''}
                  {m.usedByName ? ` · ${m.usedByName}` : ''}
                  {m.note ? ` · ${m.note}` : ''}
                </Typography>
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap' }}>
        {canManage ? (
          <>
            <Button color="error" onClick={onDelete}>
              {t('common.delete')}
            </Button>
            <Button onClick={onArchive}>{t(item.archivedAt ? 'inventory.unarchive' : 'inventory.archive')}</Button>
            <Button onClick={onEdit}>{t('common.edit')}</Button>
          </>
        ) : null}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  )
}
